import { Dayjs } from "dayjs";
import { useCallback, useEffect, useState } from "react";
import localStorageConstants from "src/app/constants/localStorage.constants";
import { EventSummary } from "src/app/models/screens/events.model";
import { RequestManagementUser } from "src/app/models/screens/requestManagement.model";
import { Task } from "src/app/models/screens/task.model";
import {
  getEventsForDate,
  getParentChildren,
  getTasksForDate,
  getUserTags,
  searchUsers,
} from "src/app/utils/backendEndpointCalls.util";

interface ParentChild {
  _id: string;
  firstName: string;
}

const ScheduleScreenViewModel = () => {
  const [isSnackbarError, setIsSnackbarError] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [isDisplayingSnackbar, setIsDisplayingSnackbar] =
    useState<boolean>(false);
  const [events, setEvents] = useState<EventSummary[]>([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState<boolean>(false);

  const [userRole, setUserRole] = useState<string | undefined>(undefined);
  const [children, setChildren] = useState<ParentChild[]>([]);
  const [isLoadingChildren, setIsLoadingChildren] = useState<boolean>(false);
  const [selectedOwnerId, setSelectedOwnerId] = useState<string>("you");
  const [adminUsers, setAdminUsers] = useState<RequestManagementUser[]>([]);
  const [isLoadingAdminUsers, setIsLoadingAdminUsers] =
    useState<boolean>(false);

  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoadingTasks, setIsLoadingTasks] = useState<boolean>(false);
  const [tags, setTags] = useState<Map<string, string>>(new Map());

  const getCurrentChildId = useCallback((): string | undefined => {
    if (userRole !== "Parent") return undefined;
    return selectedOwnerId === "you" ? undefined : selectedOwnerId;
  }, [selectedOwnerId, userRole]);

  const getCurrentAdminUserId = useCallback((): string | undefined => {
    if (userRole !== "Admin") return undefined;
    return selectedOwnerId === "you" ? undefined : selectedOwnerId;
  }, [selectedOwnerId, userRole]);

  const showError = (message: string) => {
    setIsSnackbarError(true);
    setSnackbarMessage(message);
    setIsDisplayingSnackbar(true);
  };

  const minutesToLabel = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}`;
  };

  const getUserTagsAsync = async (childId?: string, adminUserId?: string) => {
    const getTagsResponse = await getUserTags(childId ?? adminUserId);
    if (!getTagsResponse.success) return;
    const nextTags = new Map<string, string>();
    Object.keys(getTagsResponse.tags ?? {}).forEach((key: string) => {
      nextTags.set(key, getTagsResponse.tags[key]);
    });
    setTags(nextTags);
  };

  const loadTasksForDate = useCallback(
    async (date: string) => {
      setIsLoadingTasks(true);
      setIsLoadingEvents(true);
      const [tasksResponse, eventsResponse] = await Promise.all([
        getTasksForDate(date, getCurrentChildId(), getCurrentAdminUserId()),
        getEventsForDate(date, getCurrentChildId()),
      ]);
      setIsLoadingTasks(false);
      setIsLoadingEvents(false);

      if (!tasksResponse.success) {
        showError(tasksResponse.error);
      } else {
        const sorted = [...(tasksResponse.tasks ?? [])].sort(
          (a: Task, b: Task) => a.from - b.from,
        );
        setTasks(sorted);
      }

      if (!eventsResponse.success) {
        showError(eventsResponse.error);
      } else {
        const sortedEvents = [...(eventsResponse.events ?? [])].sort(
          (a: EventSummary, b: EventSummary) => a.from - b.from,
        );
        setEvents(sortedEvents);
      }
    },
    [getCurrentAdminUserId, getCurrentChildId],
  );

  const loadParentChildren = useCallback(async () => {
    setIsLoadingChildren(true);
    const response = await getParentChildren();
    setIsLoadingChildren(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setChildren(response.children ?? []);
  }, []);

  const loadAdminUsers = useCallback(async () => {
    setIsLoadingAdminUsers(true);
    const response = await searchUsers("");
    setIsLoadingAdminUsers(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setAdminUsers(response.users ?? []);
  }, []);
  useEffect(() => {
    const savedRole = localStorage.getItem(localStorageConstants.ROLE);
    if (!savedRole) return;
    setUserRole(savedRole);
  }, []);

  useEffect(() => {
    if (userRole === "Parent") {
      loadParentChildren();
    }
    if (userRole === "Admin") {
      loadAdminUsers();
    }
  }, [loadAdminUsers, loadParentChildren, userRole]);

  useEffect(() => {
    if (!selectedDate || !userRole) return;
    loadTasksForDate(selectedDate.format("YYYY-MM-DD"));
  }, [loadTasksForDate, selectedDate, selectedOwnerId, userRole]);

  useEffect(() => {
    if (!userRole) return;
    getUserTagsAsync(getCurrentChildId(), getCurrentAdminUserId());
  }, [getCurrentAdminUserId, getCurrentChildId, selectedOwnerId, userRole]);

  const onDateChanged = (date: Dayjs | null) => {
    setSelectedDate(date);
    setTasks([]);
    setEvents([]);
  };

  const onOwnerChanged = (ownerId: string) => {
    setSelectedOwnerId(ownerId);
    setTasks([]);
    setEvents([]);
  };

  return {
    isSnackbarError,
    snackbarMessage,
    isDisplayingSnackbar,
    setIsDisplayingSnackbar,
    userRole,
    children,
    isLoadingChildren,
    selectedOwnerId,
    onOwnerChanged,
    adminUsers,
    isLoadingAdminUsers,
    selectedDate,
    onDateChanged,
    tasks,
    isLoadingTasks,
    minutesToLabel,
    tags,
    events,
    isLoadingEvents,
  };
};

export default ScheduleScreenViewModel;
